'use client';

import { useEffect, useState, useCallback } from 'react';

interface ScrollHideOptions {
  threshold?: number;
  minScrollY?: number;
  debounceMs?: number;
}

/**
 * Custom hook to hide/show header based on scroll direction
 * @param options Configuration options
 * @returns Object with hidden state and scroll progress
 */
export const useScrollHide = (options: ScrollHideOptions = {}) => {
  const {
    threshold = 8,
    minScrollY = 64,
    debounceMs: _debounceMs = 10
  } = options;

  // Initialize with SSR-safe values
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lastScrollY, setLastScrollY] = useState(typeof window !== 'undefined' ? window.scrollY : 0);
  const [ticking, setTicking] = useState(false);

  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    const scrollProgress = maxScroll > 0 ? (currentScrollY / maxScroll) : 0;
    
    // Batch DOM updates for better performance
    if (typeof window !== 'undefined' && document.documentElement) {
      // Use CSS custom property for GPU-accelerated transforms
      document.documentElement.style.setProperty('--scroll-progress', scrollProgress.toFixed(3));
      
      // Add/remove classes for performance optimization
      const root = document.documentElement;
      if (currentScrollY > 10 && !root.classList.contains('scrolled')) {
        root.classList.add('scrolled');
      } else if (currentScrollY <= 10 && root.classList.contains('scrolled')) {
        root.classList.remove('scrolled');
      }
    }
    
    // Update progress state
    setProgress(scrollProgress * 100);
    
    // Update scrolled state
    setScrolled(currentScrollY > 10);
    
    // Check if scroll distance is significant enough
    if (Math.abs(currentScrollY - lastScrollY) < threshold) {
      setTicking(false);
      return;
    }
    
    // Hide on scroll down, show on scroll up
    if (currentScrollY > lastScrollY && currentScrollY > minScrollY) {
      setHidden(true);
    } else {
      setHidden(false);
    }
    
    setLastScrollY(currentScrollY);
    setTicking(false);
  }, [lastScrollY, threshold, minScrollY]);

  useEffect(() => {
    // Initialize CSS variables on mount to prevent flashing
    if (typeof window !== 'undefined' && document.documentElement) {
      document.documentElement.style.setProperty('--scroll-progress', '0');
      const currentScrollY = window.scrollY;
      if (currentScrollY > 10) {
        document.documentElement.classList.add('scrolled');
      } else {
        document.documentElement.classList.remove('scrolled');
      }
    }
    
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          setTicking(false);
        });
        setTicking(true);
      }
    };
    
    // Initial check after CSS initialization
    handleScroll();
    
    // Add scroll listener with passive flag for better performance
    window.addEventListener('scroll', onScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [handleScroll, ticking]);

  return {
    hidden,
    scrolled,
    progress,
    currentScrollY: lastScrollY
  };
};

/**
 * Hook to detect when user is at the top of the page
 */
export const useIsAtTop = (threshold = 10) => {
  // Initialize based on actual scroll position for SSR consistency
  const [isAtTop, setIsAtTop] = useState(typeof window !== 'undefined' ? window.scrollY < threshold : true);

  useEffect(() => {
    const handleScroll = () => {
      setIsAtTop(window.scrollY < threshold);
    };

    // Only run initial check on client side to avoid hydration mismatch
    if (typeof window !== 'undefined') {
      handleScroll();
    }
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [threshold]);

  return isAtTop;
};

